import type { LiveCommand, MatrixTransformCommand } from '../core/commands';
import { IDENTITY_MATRIX, type Matrix, type Point, type Rect } from '../core/geometry';
import type { EditorDocument, Layer } from '../core/model';

export interface ExtensionObjectTarget {
  id: string;
  name: string;
  bounds: Rect;
  transform: Matrix;
}

export interface InteractionTarget extends ExtensionObjectTarget {
  scope: 'object' | 'handle' | 'path-node' | 'path-segment' | 'text';
  priority: number;
  detailId?: string;
}

export interface TextEditCommand extends LiveCommand {
  updateText(document: EditorDocument, value: string): void;
}

export interface ExtensionInteractionAdapter<TLayer extends Layer = Layer> {
  hitTest(layer: TLayer, documentPoint: Point, zoom: number, parentTransform: Matrix, detailObjectIds?: ReadonlySet<string>): InteractionTarget[];
  getObject(layer: TLayer, objectId: string): ExtensionObjectTarget | null;
  createTransformCommand(layerId: string, objectId: string): MatrixTransformCommand;
  createScaleCommand?(layerId: string, objectId: string): MatrixTransformCommand;
  createMultiTransformCommand?(layerId: string, objectIds: readonly string[]): MatrixTransformCommand;
  createMultiScaleCommand?(layerId: string, objectIds: readonly string[]): MatrixTransformCommand;
  createPathNodeTransformCommand?(layerId: string, objectId: string, nodeIds: readonly string[]): MatrixTransformCommand;
  createPathSegmentTransformCommand?(layerId: string, objectId: string, segmentId: string): MatrixTransformCommand;
  createTextEditCommand?(layerId: string): TextEditCommand;
  /** Silhouette rings, in layer space, for a multi-object selection. */
  outlineSelection?(layer: TLayer, objectIds: ReadonlySet<string>): readonly (readonly (readonly [number, number])[])[];
  /** Ids wholly inside a layer-space rectangle. */
  selectWithinRect?(layer: TLayer, rect: Rect): readonly string[];
}

const adapters = new Map<Layer['kind'], ExtensionInteractionAdapter>();

export function registerExtensionInteraction<TLayer extends Layer>(kind: TLayer['kind'], adapter: ExtensionInteractionAdapter<TLayer>): () => void {
  adapters.set(kind, adapter as ExtensionInteractionAdapter);
  return () => adapters.delete(kind);
}

/**
 * `detailObjectIds` limits which objects produce node and segment targets.
 *
 * Those targets are discarded by the canvas unless node scope is active on that
 * exact object, but computing them samples every curve of every path on every
 * pointer move. Omit the set to keep the old behaviour of computing them for
 * everything.
 */
export function hitTestExtensionTargets(layer: Layer, documentPoint: Point, zoom: number, parentTransform: Matrix = IDENTITY_MATRIX, detailObjectIds?: ReadonlySet<string>): InteractionTarget[] {
  return (adapters.get(layer.kind)?.hitTest(layer, documentPoint, zoom, parentTransform, detailObjectIds) ?? []).sort((a, b) => b.priority - a.priority);
}

export function hitTestExtensionObject(layer: Layer, documentPoint: Point, zoom: number, parentTransform: Matrix = IDENTITY_MATRIX): ExtensionObjectTarget | null {
  return hitTestExtensionTargets(layer, documentPoint, zoom, parentTransform)[0] ?? null;
}

/**
 * Rings outlining a selection, or empty when the layer kind has no opinion.
 *
 * Routed through the adapter rather than imported directly so the canvas keeps
 * no runtime dependency on the vector subsystem — that lazy boundary is what
 * keeps vector code out of the startup bundle, and a static import would gut it
 * while leaving the smoke's chunk assertion green.
 */
export function outlineExtensionSelection(layer: Layer, objectIds: ReadonlySet<string>): readonly (readonly (readonly [number, number])[])[] {
  return adapters.get(layer.kind)?.outlineSelection?.(layer, objectIds) ?? [];
}

/** Objects wholly inside a layer-space rectangle. Routed through the adapter so
 * the canvas keeps no runtime dependency on the vector subsystem. */
export function selectExtensionWithinRect(layer: Layer, rect: Rect): readonly string[] {
  return adapters.get(layer.kind)?.selectWithinRect?.(layer, rect) ?? [];
}

export function getExtensionObject(layer: Layer, objectId: string): ExtensionObjectTarget | null {
  return adapters.get(layer.kind)?.getObject(layer, objectId) ?? null;
}

export function createExtensionTransformCommand(layer: Layer, objectId: string): MatrixTransformCommand | null {
  return adapters.get(layer.kind)?.createTransformCommand(layer.id, objectId) ?? null;
}

export function createExtensionScaleCommand(layer: Layer, objectId: string): MatrixTransformCommand | null {
  return adapters.get(layer.kind)?.createScaleCommand?.(layer.id, objectId) ?? null;
}

export function createExtensionMultiTransformCommand(layer: Layer, objectIds: readonly string[]): MatrixTransformCommand | null {
  return adapters.get(layer.kind)?.createMultiTransformCommand?.(layer.id, objectIds) ?? null;
}

export function createExtensionMultiScaleCommand(layer: Layer, objectIds: readonly string[]): MatrixTransformCommand | null {
  return adapters.get(layer.kind)?.createMultiScaleCommand?.(layer.id, objectIds) ?? null;
}

export function createExtensionPathNodeTransformCommand(layer: Layer, objectId: string, nodeIds: readonly string[]): MatrixTransformCommand | null {
  return adapters.get(layer.kind)?.createPathNodeTransformCommand?.(layer.id, objectId, nodeIds) ?? null;
}

export function createExtensionPathSegmentTransformCommand(layer: Layer, objectId: string, segmentId: string): MatrixTransformCommand | null {
  return adapters.get(layer.kind)?.createPathSegmentTransformCommand?.(layer.id, objectId, segmentId) ?? null;
}

export function createExtensionTextEditCommand(layer: Layer): TextEditCommand | null {
  return adapters.get(layer.kind)?.createTextEditCommand?.(layer.id) ?? null;
}

export function isTextEditCommand(command: LiveCommand): command is TextEditCommand {
  return typeof (command as Partial<TextEditCommand>).updateText === 'function';
}
